<template>
  <div class="row">
    <div class="page-title">
      <div class="row">
        <div class="col-sm-6 col-12">
          <h2 v-if="wallet">{{ wallet.riderDisplayName || 'Wallet' }}</h2>
          <p v-if="wallet" class="text-muted mb-0">
            {{ wallet.email }} &middot; {{ wallet.phone }}
          </p>
        </div>
        <div v-if="wallet" class="col-sm-6 col-12 text-end">
          <WalletFreezeModal :wallet-id="walletId" :status="wallet.status" @updated="onWalletUpdated" />
          <span class="ms-2">
            <WalletAdjustmentModal :wallet-id="walletId" @created="onRequestCreated" />
          </span>
        </div>
      </div>
    </div>

    <div v-if="isLoading" class="col-12 text-center py-4">
      <span class="spinner-border spinner-border-sm me-2"></span> Loading...
    </div>

    <template v-else-if="wallet">
      <div class="row mb-3">
        <div class="col-md-3 col-sm-6 mb-3">
          <div class="card">
            <div class="card-body">
              <h6 class="text-muted mb-1">Available Balance</h6>
              <h4 class="mb-0" v-html="minorCurrency(wallet.availableBalanceMinor)"></h4>
            </div>
          </div>
        </div>
        <div class="col-md-3 col-sm-6 mb-3">
          <div class="card">
            <div class="card-body">
              <h6 class="text-muted mb-1">Reserved</h6>
              <h4 class="mb-0" v-html="minorCurrency(wallet.reservedBalanceMinor)"></h4>
            </div>
          </div>
        </div>
        <div class="col-md-3 col-sm-6 mb-3">
          <div class="card">
            <div class="card-body">
              <h6 class="text-muted mb-1">Total</h6>
              <h4 class="mb-0" v-html="minorCurrency(wallet.totalBalanceMinor)"></h4>
            </div>
          </div>
        </div>
        <div class="col-md-3 col-sm-6 mb-3">
          <div class="card">
            <div class="card-body">
              <h6 class="text-muted mb-1">Status</h6>
              <h4 class="mb-0" v-html="walletStatus(wallet.status)"></h4>
              <p v-if="wallet.statusReason" class="text-muted small mb-0 mt-1">{{ wallet.statusReason }}</p>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12 mb-3">
        <div class="card">
          <div class="card-header"><h5 class="mb-0">Fund Categories</h5></div>
          <div class="card-body">
            <div class="row">
              <div class="col-md-3 col-sm-6 mb-2" v-for="(minor, category) in wallet.categoryBalancesMinor" :key="category">
                <div class="text-muted small">{{ category.replaceAll('_', ' ') }}</div>
                <div class="fw-bold" v-html="minorCurrency(minor)"></div>
              </div>
              <div v-if="!wallet.categoryBalancesMinor || Object.keys(wallet.categoryBalancesMinor).length === 0" class="text-muted">
                No sub-account activity yet.
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12">
        <ul class="nav nav-tabs" id="wallet-detail-tab" role="tablist">
          <li class="nav-item"><a class="nav-link active" data-bs-toggle="tab" href="#wallet-ledger" role="tab">Ledger</a></li>
          <li class="nav-item"><a class="nav-link" data-bs-toggle="tab" href="#wallet-topups" role="tab">Top-ups</a></li>
          <li class="nav-item"><a class="nav-link" data-bs-toggle="tab" href="#wallet-rides" role="tab">Linked Rides</a></li>
          <li class="nav-item"><a class="nav-link" data-bs-toggle="tab" href="#wallet-notes" role="tab">Notes</a></li>
          <li class="nav-item"><a class="nav-link" data-bs-toggle="tab" href="#wallet-audit" role="tab">Audit Log</a></li>
          <li class="nav-item"><a class="nav-link" data-bs-toggle="tab" href="#wallet-finance" role="tab">Finance</a></li>
        </ul>
        <div class="tab-content pt-3">
          <div class="tab-pane fade show active" id="wallet-ledger" role="tabpanel">
            <WalletLedgerTable :wallet-id="walletId" />
          </div>
          <div class="tab-pane fade" id="wallet-topups" role="tabpanel">
            <WalletTopUpsPanel :wallet-id="walletId" />
          </div>
          <div class="tab-pane fade" id="wallet-rides" role="tabpanel">
            <WalletRideLinksPanel :wallet-id="walletId" />
          </div>
          <div class="tab-pane fade" id="wallet-notes" role="tabpanel">
            <WalletNotesPanel :wallet-id="walletId" />
          </div>
          <div class="tab-pane fade" id="wallet-audit" role="tabpanel">
            <WalletAuditLogTable :wallet-id="walletId" />
          </div>
          <div class="tab-pane fade" id="wallet-finance" role="tabpanel">
            <WalletFinancePanel />
          </div>
        </div>
      </div>
    </template>

    <div v-else class="col-12">
      <div class="alert alert-danger">Wallet not found.</div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { minorCurrency, walletStatus } from '../utility/DatatableRenderer';
import WalletFreezeModal from './WalletFreezeModal.vue';
import WalletAdjustmentModal from './WalletAdjustmentModal.vue';
import WalletLedgerTable from './WalletLedgerTable.vue';
import WalletTopUpsPanel from './WalletTopUpsPanel.vue';
import WalletRideLinksPanel from './WalletRideLinksPanel.vue';
import WalletNotesPanel from './WalletNotesPanel.vue';
import WalletAuditLogTable from './WalletAuditLogTable.vue';
import WalletFinancePanel from './WalletFinancePanel.vue';
import { showToast, notificationType } from '../utility/toastNotificationUtility';

const props = defineProps({
  walletId: { type: String, required: true }
});

const wallet = ref(null);
const isLoading = ref(false);

const loadWallet = async () => {
  isLoading.value = true;
  try {
    const response = await fetch(`/api/admin/wallets/${props.walletId}`);
    if (response.ok) {
      wallet.value = await response.json();
    } else {
      wallet.value = null;
    }
  } catch (error) {
    console.error('Error loading wallet:', error);
    wallet.value = null;
  } finally {
    isLoading.value = false;
  }
};

const onWalletUpdated = (updated) => {
  wallet.value = updated;
};

const onRequestCreated = () => {
  showToast({
    title: 'Request submitted',
    body: 'Find it on the Wallet Approvals queue once a second admin is ready to act on it.',
    type: notificationType.SUCCESS
  });
};

onMounted(loadWallet);
</script>
